<template>
  <!-- PrimeVue hardcodes autofocus on the close button, which paints a focus ring on every toast -->
  <Toast :close-button-props="{ autofocus: false }">
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </Toast>
</template>

<script setup lang="ts">
import type { ToastEmits, ToastProps } from "primevue";

import { Toast } from "primevue";

defineProps</* @vue-ignore */ ToastProps>();
defineEmits</* @vue-ignore */ ToastEmits>();
</script>

<style lang="postcss">
.p-toast {
  @apply w-96 max-w-[calc(100vw-2.5rem)] whitespace-pre-line break-words rounded-md;

  &-message {
    @apply mb-4;

    &-icon {
      @apply mt-[0.1875rem] h-[1.125rem] w-[1.125rem] flex-shrink-0;
    }

    &-content {
      @apply flex min-h-0 items-start overflow-hidden p-3 gap-2;
    }

    &-text {
      @apply flex flex-auto flex-col gap-2;
    }

    &-info,
    &-success,
    &-warn,
    &-error,
    &-secondary,
    &-contrast {
      @apply rounded-md border shadow-xl backdrop-blur-sm;
    }

    &-info {
      @apply border-blue-200 bg-blue-50/95 text-blue-600;

      & .p-toast-detail {
        @apply text-surface-700;
      }

      & .p-toast-close-button {
        @apply hover:bg-blue-100 focus-visible:outline-blue-600;
      }
    }

    &-success {
      @apply border-green-200 bg-green-50/95 text-green-600;

      & .p-toast-detail {
        @apply text-surface-700;
      }

      & .p-toast-close-button {
        @apply hover:bg-green-100 focus-visible:outline-green-600;
      }
    }

    &-warn {
      @apply border-yellow-200 bg-yellow-50/95 text-yellow-600;

      & .p-toast-detail {
        @apply text-surface-700;
      }

      & .p-toast-close-button {
        @apply hover:bg-yellow-100 focus-visible:outline-yellow-600;
      }
    }

    &-error {
      @apply border-red-200 bg-red-50/95 text-red-600;

      & .p-toast-detail {
        @apply text-surface-700;
      }

      & .p-toast-close-button {
        @apply hover:bg-red-100 focus-visible:outline-red-600;
      }
    }

    &-secondary {
      @apply border-surface-200 bg-surface-100 text-surface-600;

      & .p-toast-detail {
        @apply text-surface-700;
      }

      & .p-toast-close-button {
        @apply hover:bg-surface-200 focus-visible:outline-surface-600;
      }
    }

    &-contrast {
      @apply border-surface-950 bg-surface-900 text-surface-50;

      & .p-toast-detail {
        @apply text-surface-0;
      }

      & .p-toast-close-button {
        @apply hover:bg-surface-800 focus-visible:outline-surface-50;
      }
    }

    &-enter-from {
      @apply translate-y-1/2 opacity-0;
    }

    &-leave-from {
      @apply max-h-[1000px];
    }

    &-enter-active {
      @apply transition duration-300 ease-in-out;
    }

    &-leave-active {
      transition:
        max-height 0.45s cubic-bezier(0, 1, 0, 1),
        opacity 0.3s,
        margin-bottom 0.3s;
    }
  }

  &-summary {
    @apply text-base font-medium;
  }

  &-detail {
    @apply text-sm font-medium;
  }

  &-close-button {
    @apply relative -end-1/4 -mt-[25%] flex h-7 w-7 cursor-pointer select-none items-center justify-center overflow-hidden rounded-full border-none bg-transparent p-0 text-inherit transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2;

    &:dir(rtl) {
      @apply end-auto ms-auto;
    }
  }

  &-close-icon {
    @apply h-4 w-4;
  }

  &-top-center {
    @apply -translate-x-1/2;
  }

  &-bottom-center {
    @apply -translate-x-1/2;
  }

  &-center {
    @apply min-w-[20vw] -translate-x-1/2 -translate-y-1/2;
  }

  & .p-toast-message.p-toast-message-leave-to {
    @apply mb-0 max-h-0 overflow-hidden opacity-0;
  }
}
</style>
